/**
 * <s-read-more> — Collapses long rich text to a short preview with a Read more / Read less toggle.
 *
 * Required child markup:
 *   [data-read-more-body]   — The text. Gets [data-collapsed] while trimmed (styled in markup).
 *   [data-read-more-toggle] — A hidden <button>; shown only when the text is longer than the preview.
 *                             Uses data-more / data-less for its labels.
 *
 * The markup should start with [data-collapsed] (and the button visible for long text) so the
 * full text never flashes before this runs; short text is un-trimmed here.
 */
class SReadMore extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const body = this.querySelector('[data-read-more-body]');
    const toggle = this.querySelector('[data-read-more-toggle]');
    if (!body || !toggle) return;

    body.toggleAttribute('data-collapsed', true);
    // Short text fits the preview already: show it in full with no button.
    if (body.scrollHeight <= body.clientHeight + 4) {
      body.removeAttribute('data-collapsed');
      toggle.hidden = true;
      return;
    }

    toggle.hidden = false;
    // Scroll position when Read more was clicked; Read less returns the shopper there.
    let scrollBeforeExpand = null;
    toggle.addEventListener(
      'click',
      () => {
        const willExpand = body.hasAttribute('data-collapsed');
        if (willExpand) scrollBeforeExpand = window.scrollY;
        const expanded = body.toggleAttribute('data-collapsed') === false;
        toggle.setAttribute('aria-expanded', String(expanded));
        toggle.textContent = expanded ? toggle.dataset.less : toggle.dataset.more;
        if (!expanded) {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          const top = scrollBeforeExpand ?? Math.max(0, this.getBoundingClientRect().top + window.scrollY - 120);
          window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
          scrollBeforeExpand = null;
        }
      },
      { signal: this.#ac.signal },
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-read-more')) customElements.define('s-read-more', SReadMore);
